/* eslint-disable no-param-reassign */
import { createSlice, createEntityAdapter, PayloadAction } from '@reduxjs/toolkit'
import { DashboardInfo } from 'schemas/DashboardInfoSchema'
import { isRoleGreaterThanOrEq, getHighestRole } from 'utils/permissionUtil'
import UserRole from 'enums/UserRole'

import { getDashboardInfos, getRecentViewedDashboardInfos } from './asyncThunks'
import { getPageConfig } from '../config/asyncThunks'
import { createUpdateScheduledReport, deleteScheduledReport } from '../scheduledReport/asyncThunks'
import { DashboardCategory, DashboardInfoBaseState } from './types'
import { dashboardInfosToLabelEntity, getDefaultStatefromSessionStorage } from './utils'
import { CATEGORY, SORT_ORDER, SORT_BY } from './constants'

export const DashboardInfoAdapter = createEntityAdapter<DashboardInfo>()

const dashboardInfoSlice = createSlice({
  name: 'dashboardInfo',
  initialState: DashboardInfoAdapter.getInitialState<DashboardInfoBaseState>({
    status: 'idle',
    error: null,
    sort: {
      order: SORT_ORDER.ASC,
      sortBy: SORT_BY.NAME
    },
    filter: {
      keyword: '',
      liveReportEnabled: undefined,
      scheduledReportEnabled: undefined
    },
    selectedLabels: [],
    labelsEntities: {},
    category: CATEGORY.myDashboards,
    ...getDefaultStatefromSessionStorage()
  }),
  reducers: {
    setSort: (state, action: PayloadAction<Partial<DashboardInfoBaseState['sort']>>) => {
      state.sort = {
        ...state.sort,
        ...action.payload
      }
    },
    setFilter: (state, action: PayloadAction<Partial<DashboardInfoBaseState['filter']>>) => {
      state.filter = {
        ...state.filter,
        ...action.payload
      }
    },
    setFilterKeyWord: (state, action: PayloadAction<string>) => {
      state.filter.keyword = action.payload
    },
    clearFilterKeyWord: (state) => {
      state.filter.keyword = ''
    },
    addLabel: (state, action: PayloadAction<string>) => {
      state.selectedLabels.push(action.payload)
    },
    removeLabel: (state, action: PayloadAction<string>) => {
      state.selectedLabels = state.selectedLabels.filter((label) => label !== action.payload)
    },
    removeAllLabels: (state) => {
      state.selectedLabels = []
    },
    setCategory: (state, action: PayloadAction<DashboardCategory>) => {
      state.category = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPageConfig.fulfilled, (state, action) => {
        const userconfig = action.payload.pageConfig.user
        const currentHighestRole = getHighestRole(userconfig?.userRoles ?? [])
        const equalOrAboveAnalyst = isRoleGreaterThanOrEq(
          currentHighestRole,
          UserRole.ROLE_BASIC_USER
        )
        // * permission below analyst category default to allDashboards
        if (!equalOrAboveAnalyst) {
          state.category = CATEGORY.allDashboards
        }
      })
      .addCase(createUpdateScheduledReport.fulfilled, (state, action) => {
        const dashboardId = action.meta.arg.id
        DashboardInfoAdapter.updateOne(state, {
          id: dashboardId,
          changes: {
            scheduledReportEnabled: true
          }
        })
      })
      .addCase(deleteScheduledReport.fulfilled, (state, action) => {
        const dashboardId = action.meta.arg
        DashboardInfoAdapter.updateOne(state, {
          id: dashboardId,
          changes: {
            scheduledReportEnabled: false
          }
        })
      })
      .addMatcher(
        (action) =>
          action.type === getRecentViewedDashboardInfos.pending.type ||
          action.type === getDashboardInfos.pending.type,
        (state) => {
          state.status = 'loading'
        }
      )
      .addMatcher(
        (action) =>
          action.type === getRecentViewedDashboardInfos.fulfilled.type ||
          action.type === getDashboardInfos.fulfilled.type,
        (state, action: PayloadAction<DashboardInfo[]>) => {
          state.status = 'succeeded'
          DashboardInfoAdapter.setAll(state, action.payload)

          const labelsEntities = dashboardInfosToLabelEntity(action.payload)
          state.labelsEntities = labelsEntities
        }
      )
      .addMatcher(
        (action) =>
          action.type === getRecentViewedDashboardInfos.rejected.type ||
          action.type === getDashboardInfos.rejected.type,
        (state, action) => {
          state.status = 'failed'
          state.error = action.error.message
        }
      )
  }
})

export const {
  setSort,
  setFilter,
  setFilterKeyWord,
  clearFilterKeyWord,
  addLabel,
  removeLabel,
  removeAllLabels,
  setCategory
} = dashboardInfoSlice.actions

export default dashboardInfoSlice.reducer
